<script setup lang="ts">
import type { PropType } from "vue";

import Checkbox from "./Checkbox.vue";

// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const props = defineProps({
    label: {
        type: String,
        required: true,
    },
    description: {
        type: String,
        required: false,
    },
    checked: {
        type: Boolean,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
        required: false,
    },
    size: {
        type: String as PropType<"md" | "sm">,
        validator: (value: string) => ["md", "sm"].includes(value),
        default: "md",
    },
    linkButtonText: {
        type: Object,
        required: false,
    },
});

const emit = defineEmits(["input"]);

const input = () => {
    emit("input");
};
</script>

<template>
    <div :class="['ds-checkbox-label', size, { disabled, checked }]" @click="input">
        <Checkbox :mode="'check'" :checked="checked" :size="size" :disabled="disabled" @input="input" />
        <div class="content-checkbox-label">
            <div class="text">
                <span class="checkbox-label regular">{{ label }}</span>
                <span v-if="description" class="checkbox-description regular">{{ description }}</span>
            </div>
            <!-- Add link button when he finished -->
            <!-- Don't forget to put the condition to display link button -->
        </div>
    </div>
</template>

<style scoped lang="scss">
.ds-checkbox-label {
    gap: 8px;
    width: max-content;
    cursor: pointer;
    display: flex;
    align-items: flex-start;

    .ds-checkbox {
        flex-shrink: 0;
        margin-top: 2px;
    }

    .content-checkbox-label {
        transition: 0.2s;
        .text {
            gap: 2px;
            display: grid;

            .checkbox-label {
                color: $text-sub-600;
                @include font-size-16;
                transition: 0.2s;
                font-weight: 400;
            }

            .checkbox-description {
                color: $text-soft-400;
                @include font-size-14;
                transition: 0.2s;
                font-weight: 400;
            }
        }
    }

    &.sm {
        .content-checkbox-label {
            .text {
                .checkbox-label {
                    @include font-size-14;
                }

                .checkbox-description {
                    @include font-size-13;
                }
            }
        }
    }

    &.checked {
        .content-checkbox-label {
            .text {
                .checkbox-label {
                    color: $text-main-900;
                }

                .checkbox-description {
                    color: $text-sub-600;
                }
            }
        }
    }

    &:hover {
        .content-checkbox-label {
            .text {
                .checkbox-label {
                    color: $text-main-900;
                }
                .checkbox-description {
                    color: $text-sub-600;
                }
            }
        }
    }

    &.disabled {
        .content-checkbox-label {
            .text {
                .checkbox-label {
                    color: $text-disabled-300;
                }
                .checkbox-description {
                    color: $text-disabled-300;
                }
            }
        }
    }
}
</style>

<style lang="scss">
.ds-checkbox-label {
    &:hover {
        .ds-checkbox {
            .wrapper-checkbox {
                background-color: $bg-weak-100;
            }
        }

        &.checked {
            .ds-checkbox {
                .wrapper-checkbox {
                    background-color: $blue-dark;
                    border-color: $blue-dark;
                }
            }
        }
    }

    &.disabled {
        cursor: default;

        &.checked {
            .ds-checkbox {
                .wrapper-checkbox {
                    background-color: $bg-soft-200;
                    border-color: $bg-soft-200;
                }
            }
        }

        .ds-checkbox {
            cursor: default;

            .wrapper-checkbox {
                background-color: $bg-white-0;
            }
        }
    }
}
</style>
